import * as PIXI from 'pixi.js';

/**
 * Определение формы для атласа частиц
 */
export type ShapeDef = {
  id: string;
  draw: (g: PIXI.Graphics, inner: number) => void; // рисуем в (0..inner, 0..inner)
};

/**
 * Интерфейс runtime-атласа
 */
export interface RuntimeAtlas {
  baseTexture: PIXI.TextureSource;
  textures: Map<string, PIXI.Texture>;
  renderTexture: PIXI.RenderTexture;
}

/**
 * Стандартные формы для частиц
 */
const defaultShapes: ShapeDef[] = [
  {
    id: 'default', // просто белый квадрат
    draw: (g, s) => {
      g.rect(0, 0, s, s).fill({ color: 0xffffff });
    },
  },
  {
    id: 'soft_circle',
    draw: (g, s) => {
      const r = s / 2 - 1;
      g.circle(s / 2, s / 2, r).fill({ color: 0xffffff });
    },
  },
  {
    id: 'bordered_square',
    draw: (g, s) => {
      const inset = Math.max(2, Math.floor(s * 0.1));
      g.rect(inset, inset, s - inset * 2, s - inset * 2).fill({ color: 0xffffff });
      g.rect(0, 0, s, s).stroke({ width: 1, color: 0xffffff });
    },
  },
  {
    id: 'star',
    draw: (g, s) => {
      const cx = s / 2,
        cy = s / 2;
      const spikes = 5;
      const outer = s / 2 - 1;
      const inner = outer * 0.5;
      const pts: PIXI.Point[] = [];
      for (let i = 0; i < spikes * 2; i++) {
        const ang = (i * Math.PI) / spikes - Math.PI / 2;
        const rad = i % 2 === 0 ? outer : inner;
        pts.push(new PIXI.Point(cx + Math.cos(ang) * rad, cy + Math.sin(ang) * rad));
      }
      g.poly(pts).fill({ color: 0xffffff });
    },
  },
  {
    id: 'triangle',
    draw: (g, s) => {
      const m = 1;
      const p1 = new PIXI.Point(s / 2, m);
      const p2 = new PIXI.Point(s - m, s - m);
      const p3 = new PIXI.Point(m, s - m);
      g.poly([p1, p2, p3]).fill({ color: 0xffffff });
    },
  },
];

/**
 * Строит runtime-атлас для частиц
 * Все формы рендерятся в один RenderTexture, а саб-текстуры делаются фреймами
 * Это позволяет любому ParticleContainer смешивать разные типы частиц
 */
export function buildRuntimeAtlas(
  renderer: PIXI.Renderer,
  opts?: {
    tileSize?: number; // размер клетки под форму, до padding
    padding?: number; // защита от bleeding
    shapes?: ShapeDef[]; // можно передать свои фигуры
    cols?: number; // кол-во колонок в атласе
  }
): RuntimeAtlas {
  const tile = Math.max(16, Math.floor(opts?.tileSize ?? 32));
  const pad = Math.max(1, Math.floor(opts?.padding ?? 2));
  const shapes = (opts?.shapes ?? defaultShapes).slice();
  const cols = Math.max(1, Math.floor(opts?.cols ?? 4));
  const rows = Math.ceil(shapes.length / cols);

  // итоговый размер: каждая плитка = tile + 2*pad
  const cell = tile + pad * 2;
  const width = cell * cols;
  const height = cell * rows;

  const rt = PIXI.RenderTexture.create({
    width,
    height,
    resolution: renderer.resolution, // чёткое DPI
  });

  // В Pixi v8 настройки фильтрации настраиваются через source
  // Пока оставляем дефолтные настройки для совместимости

  const textures = new Map<string, PIXI.Texture>();

  // Создаем контейнер для всех форм
  const container = new PIXI.Container();

  shapes.forEach((shape, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const offsetX = col * cell + pad;
    const offsetY = row * cell + pad;

    // Создаем новый Graphics для каждой формы
    const g = new PIXI.Graphics();
    shape.draw(g, tile);

    // Позиционируем Graphics в контейнере
    g.x = offsetX;
    g.y = offsetY;
    container.addChild(g);
  });

  // Рендерим весь контейнер сразу
  renderer.render({
    container: container,
    target: rt,
  });

  // Создаем текстуры для каждой формы
  shapes.forEach((shape, i) => {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const offsetX = col * cell + pad;
    const offsetY = row * cell + pad;

    const frame = new PIXI.Rectangle(offsetX, offsetY, tile, tile);
    const tex = new PIXI.Texture({ source: rt.source, frame });
    textures.set(shape.id, tex);
  });

  // Уничтожаем контейнер
  container.destroy({ children: true });

  return { baseTexture: rt.source, textures, renderTexture: rt };
}

/**
 * Уничтожает атлас и освобождает ресурсы
 */
export function destroyRuntimeAtlas(atlas: RuntimeAtlas): void {
  atlas.textures.clear();
  atlas.renderTexture.destroy();
}
